(() => {
  'use strict';
  if (customElements.get('lm-featured-gallery')) return;
  const normalize = value => String(value || '').normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const aliases = { frenchie: 'french bulldog', dobie: 'doberman', aussie: 'australian shepherd', corgi: 'pembroke welsh corgi', lab: 'labrador retriever', gsd: 'german shepherd', weim: 'weimaraner' };
  let runtimePromise;
  let runtimeFailures = 0;
  function safeAsset(value, pattern) {
    const url = new URL(value, location.href);
    if (url.origin !== location.origin && (url.protocol !== 'https:' || url.hostname !== 'cdn.shopify.com')) throw new Error('Invalid asset origin');
    if (!pattern.test(url.pathname)) throw new Error('Invalid asset path');
    return url.href;
  }
  function safeProduct(option) {
    const url = new URL(option.dataset.url, location.href);
    if (url.origin !== location.origin || !/\/products\/[a-z0-9-]+$/.test(url.pathname) || !/^\d+$/.test(option.dataset.variantId || '') || url.searchParams.get('variant') !== option.dataset.variantId) throw new Error('Invalid product route');
    return url.href;
  }
  function loadRuntime(value) {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (runtimePromise) return runtimePromise;
    const src = new URL(safeAsset(value, /\/dog-studio-model-viewer-4\.3\.1\.min\.js$/));
    if (runtimeFailures) src.searchParams.set('lm_feature_retry', String(runtimeFailures));
    runtimePromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      let settled = false;
      const finish = success => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (success) resolve();
        else { script.remove(); runtimeFailures += 1; runtimePromise = null; reject(new Error('Viewer unavailable')); }
      };
      const timer = setTimeout(() => finish(false), 15000);
      script.type = 'module';
      script.src = src.href;
      script.addEventListener('error', () => finish(false), { once: true });
      script.addEventListener('load', () => finish(!!customElements.get('model-viewer')), { once: true });
      document.head.append(script);
    });
    return runtimePromise;
  }
  class FeaturedGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.events = new AbortController();
      const signal = this.events.signal;
      this.strings = JSON.parse(this.querySelector('[data-gallery-strings]').textContent);
      this.input = this.querySelector('[data-gallery-query]');
      this.openButton = this.querySelector('[data-gallery-open]');
      this.stage = this.querySelector('[data-gallery-stage]');
      this.modelStage = this.querySelector('[data-gallery-model]');
      this.imageLink = this.querySelector('[data-gallery-image]');
      this.status = this.querySelector('[data-gallery-state]');
      this.options = [...this.querySelectorAll('[data-gallery-option]')].filter(option => {
        try {
          if (!/^\d+$/.test(option.dataset.productId || '') || !option.dataset.breed || !option.dataset.title) throw new Error('Invalid design');
          safeProduct(option);
          safeAsset(option.dataset.model, /\/dog-studio-[a-z0-9-]+-[a-f0-9]{12}\.glb$/);
          const poster = option.closest('[data-gallery-result]').querySelector('template').content.querySelector('img');
          safeAsset(poster.getAttribute('src'), /\/dog-home-[a-z0-9-]+-[a-f0-9]{12}\.png$/);
          return true;
        } catch (_) { option.closest('[data-gallery-result]').hidden = true; return false; }
      });
      this.ready = true;
      this.generation = (this.generation || 0) + 1;
      this.bindImage();
      if (!this.options.length || !this.openButton) return;
      this.selected = this.options.find(option => option.dataset.productId === this.dataset.productId);
      this.bindExternalDialog();
      document.addEventListener('shopify:section:load', () => this.bindExternalDialog(), { signal });
      document.addEventListener('shopify:section:unload', () => queueMicrotask(() => { if (this.isConnected) this.bindExternalDialog(); }), { signal });
      this.querySelector('[data-gallery-search]').hidden = false;
      this.filter();
      this.input.addEventListener('input', () => this.filter(), { signal });
      this.addEventListener('click', event => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        const option = target.closest('[data-gallery-option]');
        if (option && this.options.includes(option)) this.select(option);
        if (target.closest('[data-gallery-clear]')) { this.input.value = ''; this.filter(); this.input.focus({ preventScroll: true }); }
        if (target.closest('[data-gallery-close]')) this.close(true);
        if (target.closest('[data-gallery-open]') && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) { event.preventDefault(); this.open(); }
      }, { signal });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          if (this.opened) { event.preventDefault(); event.stopPropagation(); this.close(true); }
          else if (this.input.value) { event.preventDefault(); this.input.value = ''; this.filter(); this.input.focus({ preventScroll: true }); }
          return;
        }
        const target = event.target;
        const result = target.closest?.('[data-gallery-option]');
        if (target !== this.input && !result) return;
        if (target === this.input && event.key === 'Enter' && this.matchingOptions.length === 1) { event.preventDefault(); this.select(this.matchingOptions[0]); return; }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) || !this.matchingOptions.length) return;
        if (target === this.input && ['Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let index = this.matchingOptions.indexOf(result);
        if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = this.matchingOptions.length - 1;
        else index = target === this.input ? (event.key === 'ArrowDown' ? 0 : this.matchingOptions.length - 1) : (index + (event.key === 'ArrowDown' ? 1 : -1) + this.matchingOptions.length) % this.matchingOptions.length;
        this.roving(this.matchingOptions[index]);
        this.matchingOptions[index].focus({ preventScroll: true });
        this.matchingOptions[index].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      }, { signal });
      window.addEventListener('pagehide', () => this.close(), { signal });
    }
    disconnectedCallback() { this.events?.abort(); this.externalObserver?.disconnect(); this.externalObserver = null; this.close(); this.ready = false; }
    bindExternalDialog() {
      const discovery = document.querySelector('lm-dog-discovery');
      const dialog = discovery?.querySelector('[data-preview-dialog]');
      if (dialog === this.externalDialog && this.externalObserver) return;
      this.externalObserver?.disconnect();
      this.externalDiscovery = discovery;
      this.externalDialog = dialog;
      if (!dialog) return;
      this.externalObserver = new MutationObserver(() => { if (dialog.open && this.opened) this.close(); });
      this.externalObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    }
    roving(active) { this.options.forEach(option => { option.tabIndex = option === active ? 0 : -1; }); }
    filter() {
      const raw = normalize(this.input.value);
      const query = aliases[raw] || raw;
      this.matchingOptions = this.options.filter(option => normalize(option.dataset.breed).includes(query));
      this.options.forEach(option => { option.closest('[data-gallery-result]').hidden = !this.matchingOptions.includes(option); });
      this.roving(this.matchingOptions.includes(this.selected) ? this.selected : this.matchingOptions[0]);
      this.querySelector('[data-gallery-count]').textContent = this.strings.count.replace('[count]', String(this.matchingOptions.length));
      this.querySelector('[data-gallery-empty]').hidden = this.matchingOptions.length !== 0;
    }
    bindImage() {
      const image = this.imageLink.querySelector('img');
      const missing = this.imageLink.querySelector('[data-gallery-image-missing]');
      if (!image) return;
      const fail = () => { image.hidden = true; missing.hidden = false; };
      image.addEventListener('error', fail, { once: true, signal: this.events.signal });
      if (image.complete && image.naturalWidth === 0) fail();
    }
    select(option) {
      if (option === this.selected) return;
      const resume = this.opened;
      this.close();
      this.selected = option;
      const data = option.dataset;
      const url = safeProduct(option);
      this.dataset.productId = data.productId;
      this.dataset.breed = data.breed;
      this.dataset.model = data.model;
      this.querySelector('[data-gallery-title]').textContent = data.title;
      const variant = this.querySelector('[data-gallery-variant]');
      variant.textContent = data.variant;
      variant.hidden = data.variant === 'Default Title';
      const price = this.querySelector('[data-gallery-price]');
      price.textContent = data.price;
      price.hidden = false;
      this.querySelector('[data-gallery-availability]').hidden = data.available === 'true';
      this.querySelectorAll('[data-product-link]').forEach(link => { link.href = url; });
      this.openButton.href = url;
      this.openButton.setAttribute('aria-label', this.strings.exploreLabel.replace('[breed]', data.breed));
      this.imageLink.setAttribute('aria-label', this.strings.viewLabel.replace('[title]', data.title));
      const poster = option.closest('[data-gallery-result]').querySelector('template').content.cloneNode(true);
      const missing = document.createElement('span');
      missing.className = 'lm-featured3d__missing';
      missing.dataset.galleryImageMissing = '';
      missing.textContent = this.strings.missing;
      missing.hidden = true;
      this.imageLink.replaceChildren(poster, missing);
      this.bindImage();
      this.options.forEach(item => item.setAttribute('aria-pressed', String(item === option)));
      this.roving(option);
      this.status.textContent = this.strings.ready;
      if (resume) this.open();
    }
    close(restoreFocus = false) {
      const active = document.activeElement;
      const restoreRemovedFocus = this.modelStage?.contains(active) || active === this.querySelector('[data-gallery-close]');
      this.generation = (this.generation || 0) + 1;
      clearTimeout(this.modelTimeout);
      this.modelEvents?.abort();
      const model = this.modelStage?.querySelector('model-viewer');
      model?.removeAttribute('src');
      this.modelStage?.replaceChildren();
      if (this.modelStage) this.modelStage.hidden = true;
      this.stage?.classList.remove('is-model-ready');
      this.stage?.setAttribute('aria-busy', 'false');
      this.imageLink?.removeAttribute('aria-hidden');
      this.imageLink?.removeAttribute('tabindex');
      const tools = this.querySelector('[data-gallery-viewer-tools]');
      if (tools) tools.hidden = true;
      this.openButton?.setAttribute('aria-expanded', 'false');
      this.openButton?.removeAttribute('aria-disabled');
      this.opened = false;
      this.loading = false;
      if (this.status) this.status.textContent = '';
      if ((restoreFocus || restoreRemovedFocus) && this.isConnected) this.openButton?.focus({ preventScroll: true });
    }
    async open() {
      if (this.opened || !this.dataset.model || !this.isConnected) return;
      this.bindExternalDialog();
      this.close();
      const generation = this.generation;
      if (this.externalDialog?.open) {
        const oldTrigger = this.externalDiscovery.previewTrigger;
        this.externalDialog.addEventListener('close', () => {
          if (this.opened && generation === this.generation && document.activeElement === oldTrigger) (this.modelStage.querySelector('model-viewer') || this.openButton).focus({ preventScroll: true });
        }, { once: true, signal: this.events.signal });
        this.externalDiscovery.clearModel?.();
        this.externalDialog.close();
      }
      this.opened = true;
      this.loading = true;
      this.openButton.setAttribute('aria-expanded', 'true');
      this.openButton.setAttribute('aria-disabled', 'true');
      this.stage.setAttribute('aria-busy', 'true');
      this.querySelector('[data-gallery-viewer-tools]').hidden = false;
      this.status.textContent = this.strings.loading;
      const fail = () => {
        if (generation !== this.generation || !this.isConnected) return;
        this.close();
        this.status.textContent = this.strings.error;
      };
      try {
        const src = safeAsset(this.dataset.model, /\/dog-studio-[a-z0-9-]+-[a-f0-9]{12}\.glb$/);
        await loadRuntime(this.dataset.runtime);
        if (generation !== this.generation || !this.opened || !this.isConnected) return;
        const model = document.createElement('model-viewer');
        this.modelEvents = new AbortController();
        const signal = this.modelEvents.signal;
        model.setAttribute('alt', this.strings.modelLabel.replace('[breed]', this.dataset.breed));
        model.setAttribute('camera-controls', '');
        model.setAttribute('touch-action', 'pan-y');
        model.setAttribute('shadow-intensity', '0.6');
        model.setAttribute('interaction-prompt', 'none');
        model.setAttribute('environment-image', 'neutral');
        model.setAttribute('loading', 'eager');
        model.setAttribute('reveal', 'auto');
        model.setAttribute('tabindex', '0');
        model.addEventListener('load', () => {
          if (generation !== this.generation || !this.opened) return;
          const focusViewer = document.activeElement === this.openButton || document.activeElement === this.imageLink;
          clearTimeout(this.modelTimeout);
          this.loading = false;
          this.stage.classList.add('is-model-ready');
          this.stage.setAttribute('aria-busy', 'false');
          this.imageLink.setAttribute('aria-hidden', 'true');
          this.imageLink.setAttribute('tabindex', '-1');
          this.status.textContent = this.strings.loaded;
          if (focusViewer) model.focus({ preventScroll: true });
        }, { once: true, signal });
        model.addEventListener('error', fail, { once: true, signal });
        this.modelTimeout = setTimeout(fail, 20000);
        this.modelStage.hidden = false;
        this.modelStage.replaceChildren(model);
        model.src = src;
      } catch (_) { fail(); }
    }
  }
  customElements.define('lm-featured-gallery', FeaturedGallery);
})();
